import { Link } from "react-router-dom";
import { ArrowRight, Calendar, ShoppingBag, Target, ChevronDown } from "lucide-react";
import {
  HERO_IMAGE,
  STATS,
  COURSES,
  ABOUT_IMAGE,
  BRAND,
} from "../data/staticData";
import Reveal from "../components/Reveal";
import SectionHeader from "../components/SectionHeader";
import TestimonialsCarousel from "../components/TestimonialsCarousel";
import PackagesPreview from "../components/PackagesPreview";
import { useEffect, useState } from "react";
import api from "../lib/api";

export default function Home() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api
      .get("/products")
      .then((r) => setProducts(r.data.slice(0, 6)))
      .catch(() => {});
  }, []);

  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative min-h-[100svh] flex items-center overflow-hidden"
      >
        <div
          className="absolute inset-0 bg-center bg-cover"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        />
        <div className="absolute inset-0 hero-vignette" />
        <div className="absolute inset-0 grain-overlay" />
        <div className="absolute inset-0 moving-gradient pointer-events-none" />

        {/* HUD overlay */}
        <div className="absolute top-24 right-6 hidden md:flex flex-col items-end gap-2 z-10">
          <div className="flex items-center gap-2">
            <span className="pulse-dot" />
            <span className="overline">SYS // ONLINE</span>
          </div>
          <span className="font-mono-tactical text-xs text-zinc-400 tracking-widest">
            LAT 37.5407° // LON -77.4360°
          </span>
        </div>

        <div className="container-fld relative z-10 py-32">
          <Reveal>
            <div className="overline mb-6">// FRONTLINE DEFENSE — VETERAN OWNED</div>
          </Reveal>
          <Reveal delay={150}>
            <h1 className="font-tactical font-bold uppercase text-white text-5xl sm:text-7xl lg:text-8xl leading-[0.9] tracking-tight max-w-5xl">
              Train.
              <br />
              Carry.
              <br />
              <span className="text-[#7CFF00] glow-text">Protect.</span>
            </h1>
          </Reveal>
          <Reveal delay={250}>
            <img
              src={BRAND.logos.defendYourOwn}
              alt="Defend Your Own"
              className="mt-8 h-32 sm:h-44 md:h-52 w-auto drop-shadow-[0_0_36px_rgba(124,255,0,0.6)]"
              data-testid="hero-defend-own-mark"
            />
          </Reveal>
          <Reveal delay={300}>
            <p className="mt-6 max-w-xl text-lg text-zinc-300 leading-relaxed">
              {BRAND.tagline}. Real-world firearms training built by veterans, for
              the prepared. No theatrics. Just the calm, repeatable skill that
              keeps you and yours safe.
            </p>
          </Reveal>
          <Reveal delay={450}>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Link to="/booking" data-testid="hero-cta-book" className="btn-primary">
                <Calendar className="w-4 h-4" />
                Book Training
              </Link>
              <Link to="/merch" data-testid="hero-cta-merch" className="btn-secondary">
                <ShoppingBag className="w-4 h-4" />
                Shop Merch
              </Link>
              <Link to="/classes" data-testid="hero-cta-classes" className="btn-secondary">
                <Target className="w-4 h-4" />
                View Classes
              </Link>
            </div>
          </Reveal>
        </div>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-zinc-400 z-10">
          <span className="font-mono-tactical text-[10px] uppercase tracking-[0.3em]">
            Scroll
          </span>
          <ChevronDown className="w-4 h-4 animate-bounce text-[#7CFF00]" />
        </div>
      </section>

      {/* STATS */}
      <section data-testid="stats-section" className="relative bg-[#0A0A0A] border-y border-white/10">
        <div className="container-fld grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 100}>
              <div className="bg-[#0A0A0A] p-8 md:p-10 group hover:bg-[#151515] transition-colors h-full flex flex-col">
                {s.badge ? (
                  <>
                    <img
                      src={s.badge}
                      alt={`${s.value} ${s.label}`}
                      className="h-24 md:h-28 w-auto mb-3 drop-shadow-[0_0_18px_rgba(124,255,0,0.35)]"
                    />
                    <div className="overline">{s.label}</div>
                  </>
                ) : (
                  <>
                    <i className={`${s.icon} text-[#7CFF00] text-2xl mb-4 block`} />
                    <div className="font-tactical text-4xl md:text-5xl font-bold text-white tracking-tight">
                      {s.value}
                    </div>
                    <div className="overline mt-2">{s.label}</div>
                  </>
                )}
                <div className="mt-4 h-px w-12 bg-[#7CFF00] group-hover:w-full transition-all duration-500" />
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ABOUT */}
      <section className="container-fld py-24 grid md:grid-cols-2 gap-12 items-center">
        <Reveal>
          <div className="relative aspect-[4/5] overflow-hidden corner-bracket p-2 bg-[#0A0A0A] flex items-center justify-center">
            <img
              src={ABOUT_IMAGE}
              alt="Frontline Defense Training"
              className="max-w-[85%] max-h-[85%] w-auto h-auto object-contain drop-shadow-[0_0_40px_rgba(124,255,0,0.4)] hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent pointer-events-none" />
          </div>
        </Reveal>
        <Reveal delay={150}>
          <div className="overline mb-4">// 01 — About</div>
          <h2 className="font-tactical text-4xl md:text-6xl font-bold uppercase text-white tracking-tight leading-none">
            Built by <span className="text-[#7CFF00]">veterans.</span>
            <br />
            For the prepared.
          </h2>
          <p className="mt-6 text-zinc-400 leading-relaxed text-lg">
            Frontline Defense was founded on a simple principle:{" "}
            <span className="text-white">protection through preparation</span>.
            We're a veteran-owned, USCCA-certified training academy focused on
            building real-world capability — not theatrics.
          </p>
          <p className="mt-4 text-zinc-400 leading-relaxed">
            Whether you're brand new or experienced, our courses are designed
            around three pillars: confidence, responsibility, and preparedness.
            We meet you where you are and take you where you need to be.
          </p>
          <Link to="/about" className="btn-secondary mt-8" data-testid="about-cta-learn">
            Learn More <ArrowRight className="w-4 h-4" />
          </Link>
        </Reveal>
      </section>

      {/* COURSES */}
      <section className="bg-[#0F0F0F] border-y border-white/10 py-24">
        <div className="container-fld">
          <SectionHeader
            overline="// 02 — Featured Training"
            title="Courses Engineered for Real Life"
            subtitle="Three core programs — built to take you from first-time handler to confident concealed carrier."
          />
          <div className="grid md:grid-cols-3 gap-6">
            {COURSES.map((c, i) => (
              <Reveal key={c.id} delay={i * 100}>
                <div
                  data-testid={`home-course-${c.id}`}
                  className="tactical-card group relative overflow-hidden h-full flex flex-col"
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={c.image}
                      alt={c.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 grayscale-[35%] group-hover:grayscale-0"
                    />
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center justify-between mb-3">
                      <span className="overline">{c.level}</span>
                      <i className={`${c.icon} text-[#7CFF00]`} />
                    </div>
                    <h3 className="font-tactical text-2xl uppercase text-white font-bold leading-tight">
                      {c.name}
                    </h3>
                    <p className="text-zinc-400 text-sm mt-3 leading-relaxed flex-1">
                      {c.short}
                    </p>
                    <div className="flex items-center justify-between mt-5 pt-5 border-t border-white/10">
                      <div className="font-mono-tactical text-xs text-zinc-500 uppercase tracking-widest">
                        {c.duration}
                      </div>
                      <Link
                        to={`/classes#${c.id}`}
                        className="font-tactical uppercase tracking-wider text-sm text-[#7CFF00] hover:underline flex items-center gap-1"
                      >
                        Learn More <ArrowRight className="w-3.5 h-3.5" />
                      </Link>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="container-fld py-24">
        <SectionHeader
          overline="// 03 — Field Reports"
          title="What Our Students Say"
          subtitle="Real graduates. Real outcomes. Verified reviews from students who trained with Darrien at Frontline Defense."
        />
        <Reveal>
          <TestimonialsCarousel />
        </Reveal>
      </section>

      {/* PERMIT PACKAGES PREVIEW */}
      <PackagesPreview />

      {/* MERCH PREVIEW */}
      <section className="bg-[#0F0F0F] border-y border-white/10 py-24 overflow-hidden">
        <div className="container-fld">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
            <div>
              <div className="overline mb-3">// 04 — Tactical Merch</div>
              <h2 className="font-tactical text-4xl md:text-5xl font-bold uppercase text-white tracking-tight">
                Wear the Mindset
              </h2>
            </div>
            <Link to="/merch" className="btn-secondary" data-testid="merch-shop-cta">
              Shop All <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        <div className="relative">
          <div className="marquee-track gap-6 px-6">
            {[...products, ...products].map((p, i) => (
              <div
                key={`${p.id}-${i}`}
                className="tactical-card w-72 flex-shrink-0 group"
              >
                <div className="aspect-square bg-[#0A0A0A] overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-4">
                  <div className="overline text-[10px]">{p.category}</div>
                  <div className="font-tactical text-white uppercase tracking-wider mt-1 text-sm">
                    {p.name}
                  </div>
                  <div className="text-[#7CFF00] font-mono-tactical mt-1">
                    ${p.price.toFixed(2)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SOCIAL / CTA */}
      <section className="container-fld py-24">
        <Reveal>
          <div className="relative tactical-card p-10 md:p-16 overflow-hidden">
            <div className="absolute inset-0 moving-gradient pointer-events-none" />
            <div className="relative grid md:grid-cols-2 gap-10 items-center">
              <div>
                <div className="overline mb-3">// 05 — Stay Connected</div>
                <h2 className="font-tactical text-3xl md:text-5xl font-bold uppercase text-white tracking-tight leading-none">
                  Follow Frontline. <span className="text-[#7CFF00]">Stand Ready.</span>
                </h2>
                <p className="mt-4 text-zinc-400">
                  New range nights, drills, drops, and free training tips —
                  delivered to your feed.
                </p>
                <div className="mt-6 flex gap-3">
                  <a
                    href={BRAND.instagram}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-secondary"
                    data-testid="social-instagram"
                  >
                    <i className="fa-brands fa-instagram" /> Instagram
                  </a>
                  <a
                    href={BRAND.youtube}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-secondary"
                    data-testid="social-youtube"
                  >
                    <i className="fa-brands fa-youtube" /> YouTube
                  </a>
                </div>
              </div>
              <div className="aspect-video relative overflow-hidden border border-white/10">
                <iframe
                  data-testid="youtube-embed"
                  title="Frontline Defense"
                  src="https://www.youtube.com/embed/HpxShxNLMb0"
                  allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                />
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
